import {
  ApiRequestError,
  Button,
  Skeleton,
  formatEventDate,
  formatEventTimeWindow,
} from '@h2hspark/ui';
import { Clock, MapPin, Sparkles } from 'lucide-react';
import * as React from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { usePublicEvent } from '../event-discovery/api';
import { eventDetailPath } from '../event-discovery/constants';
import { useMyRegistration, useRegisterForEvent } from '../event-registrations/api';

import { resolveCardElements, useCheckout, useOpenCheckout, usePayForCheckout } from './api';
import { CheckoutCoverBand } from './checkout-cover-band-panel';
import { CheckoutSteps } from './checkout-steps-panel';
import {
  BACK_TO_EVENT_LABEL,
  CHECKOUT_HEADING,
  CONFIRM_FREE_RESERVATION_LABEL,
  HOLDING_A_SEAT_NOTICE,
  HOLD_EXPIRED_NOTICE,
  ORDER_SUMMARY_INCLUDES_NOTE,
  RESERVATION_DID_NOT_START_NOTICE,
  START_AGAIN_LABEL,
  TICKET_TYPE_PARAMETER,
  receiptPath,
} from './constants';
import { EventTermsPanel } from './event-terms-panel';
import { OrderItemisation } from './order-itemisation-card';
import { PaymentMethodPanel } from './payment-method-panel';
import { SeatHoldCountdown } from './seat-hold-countdown-panel';
import { TicketTypeChoicePanel } from './ticket-type-choice-panel';
import type { CardElementsHost, MemberOrder } from './types';

/**
 * Checkout: the held seat, the itemised order, the refund terms, and — for a free event — the
 * reserve action (`MEM-020` AC-2, AC-3, AC-4, AC-5, AC-6, AC-7).
 *
 * ## Transcribed from `Member App.dc.html`, the payment sheet
 *
 * "Reserve your spot" at `--text-xl`, the event and its date beneath it in `--text-muted`, then
 * the order summary card, then the Payment method section. (Since TW 44132872, 2026-09-18: the
 * title is *Reserve Your Spot*, the event line is gone, the countdown sits directly above the
 * reserve action, and the terms are one *Event Terms and Conditions* section — see `terms`.) The prototype draws it as a bottom
 * sheet over the event page; this is a route, because a sheet that holds a seat for fifteen
 * minutes and cannot be linked to is a sheet a member loses by refreshing — and `MEM-TKT-010`
 * requires a signed-out visitor to be taken through account creation and *returned to payment for
 * the same event*, which needs an address.
 *
 * ## Two deliberate deviations from the prototype, both named
 *
 * **The itemisation is one component, and its labels are the receipt's labels.** The prototype
 * itemises checkout as *Event ticket / Service fee / Total* and the order detail as *General
 * admission × 1 / Service fee / Total paid* — two orders of one thing, which is precisely what
 * `MEM-020` AC-3 forbids. The specification wins over the prototype where they disagree, so both
 * screens render `OrderItemisation` over the server's own lines. See that component's header.
 *
 * **The fee-forfeit disclosure sits inside the Refunds section, and it is not this screen's copy**
 * (`MEM-050` AC-2, AC-4). The fee CR calls `MEM-TKT-008` the single most important disclosure in
 * the document: before payment, in the same place the refund policy is shown, the member is told
 * what a refund does to the service fee and what a cancelled event returns. It arrives on the
 * checkout payload as `feeDisclosure` — one served sentence,
 * the same one the confirmation and the order record carry — and this screen renders it under the
 * policy and above the Pay control, in reading order. Nothing here composes it, so the three
 * surfaces cannot describe the terms differently.
 *
 * **There is no card form.** `MEM-021` owns payment and this story takes none, so where the
 * prototype draws card fields there is a served sentence saying payment is not switched on. Not a
 * disabled Pay button: a disabled control reads as a transient failure a member would come back
 * and retry.
 *
 * ## The seat is taken by opening this page, and given back by the clock
 *
 * On mount, if the member is not already at checkout, `openCheckout` places the hold. The
 * countdown then ticks locally against the two fields `EVT-022` serves. A reload re-reads the
 * `GET`, which hands back **the hold that exists with the time it has left** rather than a fresh
 * fifteen minutes — that is the server's behaviour, not this screen's, and it is what makes the
 * countdown honest across a refresh.
 *
 * At zero the screen stops offering the reserve action and renders the server's own sentence for
 * an expired hold, with a control that starts again. It does **not** say the event is full: the
 * seat went back to availability and may still be there, and only the server knows.
 *
 * ## A member who already holds a place never gets here (2026-09-12)
 *
 * Sumit, on a phone: *"from the reservation confirmation back on mobile if I hit back arraow I see
 * 'reserve your spot' request failed with 409. this should redirect to event detail page."*
 *
 * Two faults, and the guard below fixes the one that matters. The confirmation was reached with a
 * **push**, so Back went to the checkout the member had just spent; opening it fired `openCheckout`
 * again; and `startCheckout` refuses a member who is already confirmed with a `409` — because a
 * hold on top of their own registration would take a second seat off the room. Correct server,
 * correct client, and a raw refusal on a screen nobody should have been on.
 *
 * So: **the registration is read before the seat is asked for**, and a confirmed one redirects to
 * the event page instead of rendering anything. It is on the route rather than on the control that
 * led here, because the address is reachable from Back, a bookmark, a mailed link and a reload,
 * and only the route sees all four. The confirmation now `replace`s as well, so Back from it skips
 * the spent step where the browser can be told to — but that alone would have left the bookmark
 * and the reload broken.
 *
 * ## Telling the three 409s apart, which is why the guard is a fact and not a message
 *
 * `startCheckout` throws `ConflictError` for **three** different things: `already-registered`,
 * `no-capacity` ("This event is full") and `held-out` ("No seats free right now"). Only the first
 * is a redirect; the other two are honest refusals a member has to read. The wire carries a
 * sentence and no code, so this does not parse the sentence — it re-reads **the member's own
 * registration**, which is authoritative, and redirects only if that says `confirmed`. A sold-out
 * event still renders `RefusalNotice` exactly as before.
 *
 * That also covers the mid-flight case: a page already open when the registration lands elsewhere
 * gets its `409`, the refetch below answers `confirmed`, and the member arrives on the event page
 * rather than on an error panel — because the outcome is *you already have a place*.
 */
export function CheckoutPage({
  /**
   * The provider's hosted elements.
   *
   * A prop with a default rather than a module-level import, so a test can render this screen
   * without a provider script in the page and so the "no provider configured" state (ADR 045) is
   * reachable from a test as well as from a deployment.
   *
   * The default is `resolveCardElements()`: the development stand-in when
   * `VITE_PAYMENT_PROVIDER=mock`, the provider's own iframed fields when a publishable key is
   * configured (`PAY-004`), and `null` when neither is — in which case the checkout draws a
   * stated absence rather than a card form that cannot work. See that function's header.
   */
  cardElements = resolveCardElements(),
}: {
  readonly cardElements?: CardElementsHost | null;
} = {}): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const id = eventId ?? '';
  const navigate = useNavigate();
  /**
   * **Which ticket this member is buying, held on the URL.**
   *
   * Not in component state, and not on the seat hold. State would lose the choice on a reload,
   * which on a screen counting down fifteen minutes means a member coming back to a different
   * price than the one they left; the hold cannot carry it because a hold holds *a seat* and a
   * per-tier hold is the first row of the inventory `EVT-019` AC-3 forbids. The address is the
   * one place that is both the member's and durable.
   *
   * Absent means the organizer's first type — what the pinned reserve bar named and priced
   * (`MEM-020` AC-1) — so arriving from that bar needs no parameter at all.
   */
  const [search, setSearch] = useSearchParams();
  const chosenTicketTypeId = search.get(TICKET_TYPE_PARAMETER);
  const reader = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const event = usePublicEvent(id);
  /**
   * **The route's own guard** — read before a seat is asked for, so a member who already holds a
   * place never opens a checkout they cannot complete. See the header.
   */
  const myRegistration = useMyRegistration(id, { enabled: id.length > 0 });
  const alreadyRegistered = myRegistration.data?.registration?.status === 'confirmed';
  const checkoutQuery = useCheckout(id, {
    enabled: id.length > 0,
    ticketTypeId: chosenTicketTypeId,
  });
  const openCheckout = useOpenCheckout(id);
  const register = useRegisterForEvent(id);
  const pay = usePayForCheckout(id);
  const [holdExpired, setHoldExpired] = React.useState(false);

  const checkout = checkoutQuery.data?.checkout ?? null;
  const opened = openCheckout.data?.checkout ?? null;
  /**
   * **The query cache first**, and the mutation's own answer only as a fallback.
   *
   * This was `opened ?? checkout`, which was correct while a checkout could only ever be for one
   * ticket: the `POST` seeds the very key the `GET` reads, so the two were the same value. With a
   * selector they are not. `openCheckout.data` is whatever the *last* switch returned and it is
   * never re-keyed, so preferring it would render the previous tier's total against the newly
   * chosen tier's radio — on the one screen where a stale number is a member agreeing to the
   * wrong amount.
   *
   * `checkout` is keyed on the current selection by construction, so it cannot be the wrong tier.
   */
  const current = checkout ?? opened;

  /**
   * Open checkout exactly once, and only once the `GET` has answered.
   *
   * The order matters: firing the `POST` before knowing whether this member is already at checkout
   * would be harmless on the server — `EVT-022` hands the same hold back — but it would spend a
   * write on every render of a page a member reloaded, and `openCheckout` is the one call here
   * that takes a seat off the room.
   */
  const shouldOpen =
    id.length > 0 &&
    checkoutQuery.isSuccess &&
    checkout === null &&
    !openCheckout.isPending &&
    /* **Never before the guard has answered, and never after it has fired.** Firing the `POST`
       while the registration read is in flight is the defect itself: it is the call that earns
       the `409`, and on a redirect it would take a write and a refusal for a screen that is about
       to unmount.

       **Settled, not succeeded** — TW 44159509
       (https://ten243.teamwork.com/app/tasks/44159509). This was `myRegistration.isSuccess`, so a
       read that *failed* held the reservation up for ever: `shouldOpen` never became true, no seat
       was ever asked for, and the screen sat on the wordless state below. Brad met it on an
       account created seconds earlier, which is the account most likely to have that read refused.

       Waiting for an answer is right; requiring a *particular* answer is not. The guard exists to
       spare a member a refusal, and the server is the authority on whether they already have a
       place: `startCheckout` refuses one with a `409`, and the conflict handler below re-reads the
       registration and redirects when it comes back `confirmed`. So opening on a failed check
       costs at worst one refused write that resolves into the event page — against a member losing
       the whole flow with nothing on the screen to tell them. */
    (myRegistration.isSuccess || myRegistration.isError) &&
    !alreadyRegistered;
  React.useEffect(() => {
    if (!shouldOpen || openCheckout.isSuccess || openCheckout.isError) return;
    openCheckout.mutate(chosenTicketTypeId);
  }, [shouldOpen, openCheckout, chosenTicketTypeId]);

  /**
   * **A conflict re-asks the one question that decides what it meant.**
   *
   * `409` is `already-registered`, `no-capacity` or `held-out`, and the wire carries no code to
   * tell them apart. Rather than reading the sentence, this re-reads the member's registration: if
   * it now says `confirmed`, the guard above redirects on the next render; if it does not, the
   * refusal was about seats and the screen renders it unchanged.
   */
  const conflicted =
    openCheckout.error instanceof ApiRequestError && openCheckout.error.statusCode === 409;
  const refetchRegistration = myRegistration.refetch;
  React.useEffect(() => {
    if (!conflicted) return;
    void refetchRegistration();
  }, [conflicted, refetchRegistration]);

  /**
   * The member picks a different ticket.
   *
   * Two acts, in this order: put the choice on the URL — so a reload from here comes back to the
   * ticket they just picked — and re-`POST` the checkout to have the order priced again. The
   * `POST` **does not extend the hold**: `EVT-022` hands back the live one with the time it has
   * left, which is the same property a reload has always relied on, so switching tiers costs the
   * member none of their fifteen minutes.
   *
   * `replace` rather than a push, because a member tapping through three tiers should not have to
   * press back three times to leave checkout.
   */
  const chooseTicketType = React.useCallback(
    (ticketTypeId: string) => {
      setSearch({ [TICKET_TYPE_PARAMETER]: ticketTypeId }, { replace: true });
      openCheckout.mutate(ticketTypeId);
    },
    [openCheckout, setSearch],
  );

  /**
   * The skeleton, and it is drawn on a **tier switch** as well as on a first load — deliberately.
   *
   * Switching moves to a query key that has no data yet, so this branch is taken for the length of
   * one round trip. The alternative is to keep the previous tier's answer on screen
   * (`placeholderData`), and that is worse here than a flicker: it puts one tier's total under
   * another tier's selected radio, which is the exact class of thing this whole selector exists to
   * prevent. A member reading a price must never be reading a price for something else.
   */
  /*
    **The guard, above every other branch including the refusal.** A member with a place is sent to
    the event page — where their ticket, their matches and Who's coming are — with `replace`, so
    the checkout they were bounced off does not sit in history waiting for another Back.
  */
  /*
    **Unless the place is the one this screen has just confirmed** — TW 44108653
    (https://ten243.teamwork.com/app/tasks/44108653). Confirming refreshes every
    registration-dependent read *before* the mutation settles (`refreshRegistrationDependentReads`
    is awaited in its `onSuccess`), so this guard's own read comes back `confirmed` while the
    Confirm or Pay call is still pending. Redirecting to the event page then unmounted the screen
    before its `navigate(receiptPath(id))` could run: every first reservation skipped the
    confirmation and landed on the event page. A place this screen is confirming goes to the
    confirmation, whichever of the two gets there first.
  */
  const confirmedHere = register.isPending || register.isSuccess || pay.isPending || pay.isSuccess;
  if (alreadyRegistered) {
    return <Navigate to={confirmedHere ? receiptPath(id) : eventDetailPath(id)} replace />;
  }

  /*
    **The guard's own read is not in this condition, deliberately.** Blocking the whole screen on
    it would make checkout wait for a second round trip it does not need — the redirect above
    fires the moment the answer arrives, and until it does the worst case is a member seeing the
    checkout they asked for. What the read *does* gate is `shouldOpen`, which is the call that
    takes a seat; nothing is spent before the guard has answered.
  */
  if (event.isPending || checkoutQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  const refusal = openCheckout.error ?? null;
  /*
    A conflict whose meaning is still being re-read draws the skeleton rather than the refusal.
    Showing "This event is full" for the half-second before the registration comes back
    `confirmed` would be the wrong sentence, briefly, on the screen this fix exists to keep a
    member off.
  */
  if (conflicted && myRegistration.isFetching) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }
  if (refusal !== null) {
    // The server's own sentence — "This event is full", "No seats free right now — someone's at
    // checkout", "Registration for this event has closed". The screen surfaces what was actually
    // applied rather than composing its own, which is the case that matters: the event page said
    // there was room and the last seat went between the render and the tap.
    return (
      <div className="space-y-4">
        <BackToEvent eventId={id} />
        <h1 className="text-2xl">{CHECKOUT_HEADING}</h1>
        <RefusalNotice error={refusal} />
      </div>
    );
  }

  /**
   * **No checkout in hand — and it is never wordless** (TW 44159509).
   *
   * This drew a back link and a bare `Skeleton`, permanently, with nothing to read. That is the
   * screen on Brad's report: the member shell, *Back to event*, one empty grey box. A skeleton is
   * a promise that something is coming, and this branch is reached in states where nothing is.
   *
   * So it is two states now, and both of them speak:
   *
   * - **something is coming** — the `POST` that takes the seat is out, or the registration read
   *   the `POST` waits on still is — and the screen says so above the skeleton, in a live region,
   *   so a member who cannot see the box is told as well;
   * - **nothing is coming** — the calls have settled and there is still no checkout — and the
   *   screen says no seat is being held and gives two ways on: ask again, or go back to the event.
   *
   * The heading is here in both, because a member who has landed on something they cannot act on
   * should at least be told which screen it is.
   */
  if (current === null) {
    const reservationInFlight = openCheckout.isPending || myRegistration.isPending;
    return (
      <div className="space-y-4">
        <BackToEvent eventId={id} />
        <h1 className="font-brand text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {CHECKOUT_HEADING}
        </h1>
        {reservationInFlight ? (
          <>
            <p role="status" className="text-sm text-muted-foreground">
              {HOLDING_A_SEAT_NOTICE}
            </p>
            <Skeleton className="h-40 w-full rounded-lg" />
          </>
        ) : (
          <div className="space-y-2">
            <p role="status" className="text-sm text-destructive">
              {RESERVATION_DID_NOT_START_NOTICE}
            </p>
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                openCheckout.mutate(chosenTicketTypeId);
              }}
            >
              {START_AGAIN_LABEL}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const detail = event.data ?? null;

  /**
   * *Venue, City* for the summary card — the two parts of §3.1's location row that are public.
   *
   * Built by joining what is there rather than by a template with a comma in it, so an event that
   * names a venue and no city reads *"Bell Street Studio"* and not *"Bell Street Studio, "*.
   * `null` when it would say nothing, which is the row's own absence rather than an empty line
   * with a pin beside it.
   */
  const venueLine =
    [detail?.location.venueName, detail?.location.city]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .join(', ') || null;

  /**
   * The summary card's eyebrow — §3.1's `{{ ev.date }}`, the day alone — and, under the name, the
   * hours **with the end** (TW 44132872, change 5). The header used to carry the long form in a
   * subheading; that line is gone (change 2), so this card is the one place the checkout states
   * when the event is, and it states all of it.
   */
  const cardDate =
    detail === null ? null : formatEventDate({ startsAt: detail.startsAt, timezone: detail.timezone });
  const cardHours =
    detail === null
      ? null
      : formatEventTimeWindow({
          startsAt: detail.startsAt,
          endsAt: detail.endsAt,
          timezone: detail.timezone,
          reader,
        });

  /**
   * **Event Terms and Conditions** — the refund terms, and on a paid order the fee disclosure
   * (`MEM-020` AC-7's first clause, `MEM-050` AC-2), in the one section the confirmation draws too.
   *
   * **Where it goes depends on whether money is taken**, and that is the one place TW 44132872's
   * change 4 is not applied whole. Brad asked for the terms below the reserve action, and on a free
   * checkout they are: nothing is charged, there is no disclosure (`MEM-050` AC-5), and the terms
   * sit where the confirmation has them. On a paid checkout they stay **above the Pay control**,
   * because `MEM-058` AC-3 and `MEM-050` AC-2 require the terms and the fee disclosure to precede
   * the control that takes payment, in the document, at every width — the one disclosure the fee
   * CR calls the most important in the document. Moving them there too would reword `MEM-058` AC-3,
   * and that is raised for a decision rather than taken here.
   *
   * Defined here, above the payment section, so the source order states the rule as well as the
   * render does (`the-checkout-at-two-widths.test.tsx`).
   */
  const terms = (
    <EventTermsPanel
      policySentence={current.refundPolicy?.sentence ?? current.refundPolicyNotice ?? ''}
      feeDisclosure={current.feeDisclosure}
    />
  );
  const paid = current.order.paymentRequired;

  /**
   * ── Two panes on desktop, one column on a phone (`MEM-056` AC-1, AC-2) ────────────────────
   *
   * `Web App.dc.html`'s reservation flow is `1.5fr 1fr` at 1080px: the steps down the left, the
   * order summary sticky on the right, at every step of the flow. What was here was the 390px
   * phone column stretched to the desktop shell's full 1180px — one very long line of controls
   * with the summary somewhere in the middle of it.
   *
   * **`lg`, not `md`**, for the event page's measured reason (`MEM-056`'s Notes): at 768 the
   * canvas is 704px and a `1.5fr 1fr` split leaves the summary 268px, narrower than its own total
   * line. It is one column until there is room for two.
   *
   * **One element, moved — never two hidden from each other.** The summary is the same
   * `OrderItemisation` node in both layouts; a `lg:hidden` copy beside a `hidden lg:block` copy
   * would be two order summaries that can disagree, and `the-responsive-behaviour.test.ts` fails
   * the whole suite for exactly that shape. Reflow needs no entry in `responsive-behaviour.ts`:
   * everything is reachable at every width.
   *
   * **The reading order is preserved where it is load-bearing.** The refund terms and the service
   * fee disclosure stay above the Pay control *in the document* (`MEM-020` AC-7, `MEM-050` AC-2,
   * and a test that asserts the indices rather than the presence), because both panes' contents
   * are in the left column and the summary is a sibling after it.
   */
  return (
    <div className="space-y-5 pb-4">
      <BackToEvent eventId={id} />

      {/*
        ── §3.1's step header ──────────────────────────────────────────────────────────────

        The stepper, then the heading at `--text-3xl` with `--tracking-tight`, then the event and
        its date in muted ink. The prototype's own order and its own type scale; what was here was
        a `text-xl` heading with no indication that this was step one of anything.

        **Not §3.1's page chrome.** The prototype replaces the whole shell with a logotype-and-
        Cancel bar, because its reservation flow is a sign-up funnel for somebody who has no
        account and no navigation to keep. Ours is a route inside the member shell for somebody
        who is signed in — taking their navigation away for two screens would strand them, and
        *Back to event* above is the Cancel the prototype's bar carries. Named in the PR.
      */}
      <header className="space-y-3">
        <CheckoutSteps current="reserve" />
        {/* No event line under the title (TW 44132872, change 2): the summary card beside it
            names the event, its date and its hours. */}
        <h1 className="font-brand text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {CHECKOUT_HEADING}
        </h1>
      </header>

      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-5">
          {/* **Which ticket**, above the summary that prices it — the order a member reads them in
          is the order they decide in. Nothing is drawn for a one-ticket event or a free one; see
          the panel's own header. */}
          <TicketTypeChoicePanel
            choices={current.ticketChoices}
            selectedTicketTypeId={current.order.ticketTypeId}
            onChoose={chooseTicketType}
            /* While a re-price is in flight, so two rapid taps cannot queue two opens — and once the
           hold has run out, because changing your ticket on a seat that went back is a decision
           about nothing. The member's control in that state is Start again, below. */
            disabled={openCheckout.isPending || holdExpired}
          />

          {/* A paid checkout's terms, ahead of the countdown and the Pay control — see `terms`. */}
          {paid ? terms : null}

          {/* `MEM-020` AC-2's visible countdown, and `EVT-022` AC-2's third clause. It ticks
              locally; it does not poll. **Directly above the reserve action**, in its own white
              card and larger type (TW 44132872, change 3): the last thing a member reads before
              committing is how long the seat is theirs. */}
          {holdExpired ? null : (
            <SeatHoldCountdown
              hold={current.hold}
              onExpired={() => {
                setHoldExpired(true);
              }}
            />
          )}

          {/* `MEM-020` AC-6: **a free event's checkout has no payment section at all.** Not an empty
          one and not an explanation — a member who owes nothing has no reason to wonder where the
          card form went. `paymentRequired` is the server's answer, derived from the order's total
          rather than from the event's pricing model, so a fully-comped paid order behaves like a
          free one for the same reason.

          `MEM-021` AC-1, AC-2 and AC-3 live inside `PaymentSection`: the cards this member has
          paid with, the provider's own iframed fields, and the one control that takes money. The
          hold has to be alive for any of it to mean anything, so the section goes with the
          countdown. */}
          {!current.order.paymentRequired || holdExpired ? null : (
            <PaymentMethodPanel
              total={totalAmountOf(current.order)}
              savedCards={current.savedCards}
              elements={cardElements}
              pending={pay.isPending}
              failure={pay.error ?? null}
              onPay={(paymentMethodToken) => {
                pay.mutate(
                  {
                    paymentMethodToken,
                    // What the **server** said this order is for and what it costs, read back off
                    // the itemisation this screen is rendering — never the URL parameter and never a
                    // number composed here. So the price sent as agreed is, by construction, the
                    // price the member is looking at.
                    ticketTypeId: current.order.ticketTypeId,
                    agreedTicketPriceCents: ticketAmountOf(current.order),
                    // And the terms rendered under *Refunds* above, echoed unchanged. Off the same
                    // payload the section three blocks up renders, so what is sent as agreed is by
                    // construction a fingerprint of what the member is looking at — never a second
                    // read and never a value this screen composed.
                    agreedRefundTermsFingerprint: current.refundTermsFingerprint,
                  },
                  {
                    onSuccess: () => {
                      /* `replace`: the checkout is spent, and Back from the confirmation should
                         be the event rather than a screen that would refuse them (2026-09-12). */
                      navigate(receiptPath(id), { replace: true });
                    },
                  },
                );
              }}
            />
          )}

          {holdExpired ? (
            <div className="space-y-2">
              <p role="status" className="text-sm text-destructive">
                {HOLD_EXPIRED_NOTICE}
              </p>
              <Button
                className="w-full"
                size="lg"
                disabled={openCheckout.isPending}
                onClick={() => {
                  setHoldExpired(false);
                  openCheckout.mutate(chosenTicketTypeId);
                }}
              >
                {START_AGAIN_LABEL}
              </Button>
            </div>
          ) : paid ? /* A paid checkout's action is the Pay control inside `PaymentMethodPanel` above,
               beside the fields it needs. A second action down here would be two controls for one
               decision, which is the arrangement `MEM-020` AC-1 refused on the event page. */
          null : (
            <div className="space-y-2">
              <Button
                /* The same control §3.1 gives its pay action, minus the lock: this is the button
                   that completes the reservation, and a free event's member is agreeing to the
                   same thing for nothing. No padlock, because nothing is being encrypted and
                   nothing is being charged — the icon would be reassurance about a transaction
                   that is not happening. */
                className="w-full rounded-pill bg-spark text-white shadow-accent hover:bg-spark sm:py-[15px] sm:text-md"
                size="lg"
                disabled={register.isPending}
                onClick={() => {
                  register.mutate(undefined, {
                    onSuccess: () => {
                      // The free path's twin of the same `replace` — see above.
                      navigate(receiptPath(id), { replace: true });
                    },
                  });
                }}
              >
                {CONFIRM_FREE_RESERVATION_LABEL}
              </Button>
              {register.error === null ? null : <RefusalNotice error={register.error} />}
            </div>
          )}

          {/* A free checkout's terms, **below** the reserve action, where the confirmation has them
              (TW 44132872, change 4) — see `terms` for why a paid checkout's are not. */}
          {paid ? null : terms}
        </div>

        {/*
          The order summary — the design's right-hand pane.

          `MEM-020` AC-3, AC-4, AC-5; `MEM-050` AC-1. **The same component the receipt renders**,
          over the server's own lines: the ticket, one Service fee and the total, the fee stated at
          $0.00 under a zero-rate record rather than hidden. It is handed no `className`, and that
          is not fussiness — `one-itemisation-two-screens.test.tsx` compares this element's
          `outerHTML` byte for byte against the receipt's, so a sticky class on one of the two
          screens would make the two summaries provably different markup. The positioning is on
          the wrapper.

          Sticky from `lg` at the shell's own header offset, as the prototype's summary is, so a
          member scrolling a long payment form keeps the total in view — which is the reason the
          design put it in a column of its own.

          **The card is §3.1's**: `--radius-xl` over `--shadow-md`, a `--border-subtle` hairline, a
          band of the spark gradient across the top, then the event's date as a `--brand-accent`
          eyebrow, its name, where it is, and the lines under a `--divider` rule. It is shorter on a
          phone, where the card is full-width and a 120px band above the total would be the loudest
          thing on the screen.

          **The band is the event's cover** — TW 44108645
          (https://ten243.teamwork.com/app/tasks/44108645). The prototype draws a gradient there as
          its stand-in for an event image, and this card copied the stand-in when no event had a
          cover. Events have covers now (`EVT-008`), so the band shows the organizer's own image,
          read off the same public payload and the same `coverImage` the event page renders, with
          its description as the text alternative (`EVT-008` AC-2). The gradient is kept for an
          event with no cover — the real state `PublicCoverImage | null` names.
        */}
        <div className="min-w-0 overflow-hidden rounded-xl border border-border-subtle bg-card shadow-md lg:sticky lg:top-[88px]">
          <CheckoutCoverBand cover={detail?.coverImage ?? null} />
          <div className="space-y-1 p-4 sm:p-5">
            {detail === null ? null : (
              <>
                <p className="font-brand text-xs font-bold uppercase tracking-caps text-brand-accent">
                  {cardDate}
                </p>
                <h2 className="font-brand text-lg font-bold text-foreground">{detail.name}</h2>
                {/* When it starts **and ends** (TW 44132872, change 5), in the event's own zone. */}
                {cardHours === null || cardHours === '' ? null : (
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="h-[15px] w-[15px] shrink-0" aria-hidden />
                    <span>{cardHours}</span>
                  </p>
                )}
                {/* §3.1's `{{ ev.venue }}, {{ ev.city }}` row. **Not the address**: the street
                    line and the map links are the two things `MEM-016` AC-3 withholds until a
                    member is registered, and the server withholds them by serving `null` rather
                    than by anybody here remembering to. The venue's name and the city are on the
                    public listing already, so this row says no more than the card that sent them
                    here. Absent entirely for an event that names neither. */}
                {venueLine === null ? null : (
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="h-[15px] w-[15px] shrink-0" aria-hidden />
                    <span>{venueLine}</span>
                  </p>
                )}
              </>
            )}
            <div className="!mt-4 border-t border-divider pt-3.5">
              <OrderItemisation order={current.order} />
            </div>
            {/* §3.1's note under the totals, restored in the parity pass — see
                `ORDER_SUMMARY_INCLUDES_NOTE`. Outside `OrderItemisation` on purpose:
                `one-itemisation-two-screens.test.tsx` compares that element byte for byte against
                the receipt's, and the receipt is not selling anything. */}
            <p className="!mt-4 flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2.5 text-xs text-brand">
              <Sparkles className="h-[15px] w-[15px] shrink-0" aria-hidden />
              {ORDER_SUMMARY_INCLUDES_NOTE}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BackToEvent({ eventId }: { readonly eventId: string }): React.ReactElement {
  return (
    <PageBackLink to={`/app/events/${encodeURIComponent(eventId)}`}>
      {BACK_TO_EVENT_LABEL}
    </PageBackLink>
  );
}

/** The server's own sentence, shown as written. The same component `RegistrationPanel` uses, and
 * the same argument: what a member reads is what the server actually applied. */
/**
 * The server's own refusal, in the server's own words.
 *
 * **`toUserMessage()`, not `.message`** — the third fault in Sumit's 2026-09-12 report and the one
 * that made the other two visible. `ApiRequestError.message` is `apiRequest`'s own
 * *"Request failed with status 409."*; the sentence the API actually applied — *"This event is
 * full."*, *"You're already registered for this event."* — is in the envelope, and
 * `toUserMessage()` is the accessor that reads it. This component's header has claimed to render
 * the server's sentence since it was written and rendered the status line instead, which is
 * literally what he quoted: *"'reserve your spot' request failed with 409"*.
 */
function RefusalNotice({ error }: { readonly error: Error }): React.ReactElement {
  const served = error instanceof ApiRequestError ? error.toUserMessage() : '';
  const message =
    served.length > 0 && !/^Request failed with status /u.test(served)
      ? served
      : 'Something went wrong on our side. Try again in a moment.';
  return (
    <p role="status" className="text-sm text-destructive">
      {message}
    </p>
  );
}

/**
 * The total line's rendered amount, for the pay control's label — **the total including the
 * service fee**, which is what the card is charged (`MEM-021` AC-4, `MEM-TKT-009`).
 *
 * Read off the server's own itemisation by `kind` rather than by position, the same discipline
 * `OrderItemisation` keeps: a line added on the server must not make this read the wrong one.
 * `''` when there is no total, which cannot happen — `itemiseOrder` always emits one — and which
 * would render "Pay" rather than "Pay undefined" if it did.
 */
function totalAmountOf(order: {
  readonly lines: ReadonlyArray<{ readonly kind: string; readonly amount: string }>;
}): string {
  return order.lines.find((line) => line.kind === 'total')?.amount ?? '';
}

/**
 * The ticket line's own amount, off the server's itemisation.
 *
 * Read by `kind` rather than by position, the same discipline `totalAmountOf` and
 * `OrderItemisation` keep: a fifth line added on the server must not make this the wrong one.
 *
 * It is the **ticket** line and not the total, because the guard it feeds is about the ticket
 * type's price — the one figure an organizer can move under a member mid-checkout. A fee schedule
 * changing between the summary and the charge is `FIN-009`'s rule about applied rates and is not
 * this screen's to police.
 *
 * `-1` when there is no ticket line, which `itemiseOrder` cannot produce — it always emits one.
 * A negative is refused by the API's own `nonnegative()` rather than silently agreeing to zero,
 * which is the value a `?? 0` would have sent.
 */
function ticketAmountOf(order: MemberOrder): number {
  return order.lines.find((line) => line.kind === 'ticket')?.amountCents ?? -1;
}
